import { createContext, type ReactNode, useContext } from "react";
import { cn } from "../lib/utils";

/** Views of a combined left tab: the title bar shows them as a switch instead of the title. */
export interface PanelSwitch<T extends string = string> {
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
}

export const PanelSwitchContext = createContext<PanelSwitch | null>(null);

/** Title bar of a left panel. */
export function PanelTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  const switcher = useContext(PanelSwitchContext);
  return (
    <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-zinc-100 px-4">
      {switcher && switcher.options.length > 1 ? (
        <div role="tablist" className="flex min-w-0 flex-1 rounded-lg bg-zinc-100 p-0.5">
          {switcher.options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={switcher.value === option.id}
              onClick={() => switcher.onChange(option.id)}
              className={cn(
                "flex-auto rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap text-zinc-600 hover:text-zinc-900",
                switcher.value === option.id && "bg-white text-zinc-900 shadow-sm",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : (
        <h2 className="truncate text-sm font-semibold text-zinc-900">{children}</h2>
      )}
      {actions}
    </div>
  );
}
